import { useState, useEffect } from 'react';
import { employeeApi } from '../services/api';
import { toast } from 'sonner';

export function useEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEmployees = async (params) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading employees with token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      
      const response = await employeeApi.getAll(params);
      if (response.success && response.data) {
        // El backend devuelve { employees: [...], totalPages, currentPage, total }
        const employees = response.data.employees || response.data;
        const mappedEmployees = Array.isArray(employees) ? employees.map(emp => ({
          ...emp,
          // ID unificado
          id: emp._id || emp.id,
          // Mapear propiedades para compatibilidad (SIN CAMBIAR CÁLCULOS)
          start_date: emp.startDate || emp.start_date,
          legal_days: emp.legalDays || emp.legal_days,
          progressive_days: emp.progressiveDays || emp.progressive_days,
          additional_days: emp.additionalDays || emp.additional_days,
          administrative_days: emp.administrativeDays || emp.administrative_days,
          accumulated_days: emp.accumulatedDays || emp.accumulated_days,
          used_days: emp.usedDays || emp.used_days,
          last_admin_reset: emp.lastAdminReset || emp.last_admin_reset,
          // USAR LOS VALORES ORIGINALES DEL BACKEND
          vacation_days_available: emp.vacationDaysAvailable || emp.vacation_days_available || emp.accumulatedDays || 0,
          admin_days_available: emp.adminDaysAvailable || emp.admin_days_available || emp.administrativeDays || 0,
          admin_days_used_this_year: emp.adminDaysUsedThisYear || emp.admin_days_used_this_year || 0
        })) : [];
        setEmployees(mappedEmployees);
      } else {
        throw new Error(response.message || 'Error al cargar empleados');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error loading employees:', err);
      
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        toast.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
      } else {
        toast.error('Error al cargar empleados: ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const addEmployee = async (employeeData) => {
    const response = await employeeApi.create(employeeData);
    if (response.success && response.data) {
      setEmployees(prev => [...prev, response.data]);
      toast.success(response.message || 'Empleado creado exitosamente');
      return response.data;
    }
    throw new Error(response.message || 'Error al crear empleado');
  };

  const updateEmployee = async (id, updates) => {
    const response = await employeeApi.update(id, updates);
    if (response.success && response.data) {
      setEmployees(prev => prev.map(emp => emp.id === id ? response.data : emp));
      toast.success(response.message || 'Empleado actualizado exitosamente');
      return response.data;
    }
    throw new Error(response.message || 'Error al actualizar empleado');
  };

  const deleteEmployee = async (id) => {
    const response = await employeeApi.delete(id);
    if (response.success) {
      setEmployees(prev => prev.filter(emp => emp.id !== id));
      toast.success(response.message || 'Empleado eliminado exitosamente');
      return true;
    }
    throw new Error(response.message || 'Error al eliminar empleado');
  };

  const refreshAccumulatedDays = async () => {
    const response = await employeeApi.recalculateAccumulatedDays();
    if (response.success) {
      await loadEmployees();
      toast.success(response.message || 'Días acumulados recalculados');
    }
  };

  const checkAndResetAdministrativeDays = async () => {
    try {
      const response = await employeeApi.resetAdministrativeDays();
      if (response.success) {
        await loadEmployees();
      }
    } catch (err) {
      console.error('Error al resetear días administrativos:', err);
    }
  };

  const addDaysToEmployee = async (employeeId, type, days, reason) => {
    console.log('useEmployees: addDaysToEmployee llamado con:', {
      employeeId, type, days, reason
    });
    
    try {
      const response = await employeeApi.addDays(employeeId, { 
        type, 
        days, 
        reason 
      });
      
      console.log('useEmployees: Respuesta del API:', response);
      
      if (response.success) {
        await loadEmployees(); // Recargar empleados
        toast.success(response.message || 'Días agregados exitosamente');
        return response.data;
      } else {
        console.error('useEmployees: Error en respuesta:', response);
        throw new Error(response.message || 'Error al agregar días');
      }
    } catch (error) {
      console.error('useEmployees: Error en addDaysToEmployee:', error);
      toast.error('Error al agregar días: ' + (error.message || 'Error desconocido'));
      throw error;
    }
  };

  return {
    employees,
    loading,
    error,
    loadEmployees,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    refreshAccumulatedDays,
    checkAndResetAdministrativeDays,
    addDaysToEmployee
  };
}
